import { useState, useEffect, useRef } from 'react';
import { Search, X, User, Phone, CheckCircle2, LogOut, Loader2, ChevronLeft, MapPin } from 'lucide-react';
import { supabase } from '../utils/supabase';
import { API_BASE_URL } from '../utils/apiConfig';
import toast from 'react-hot-toast';

interface Member {
  id: string;
  fullName: string;
  displayName?: string;
  phoneNumber: string;
  status: string;
  effectiveStatus?: string;
  profilePhoto?: string;
}

interface MemberAttendanceModalProps {
  onClose: () => void;
  branchId?: string;
  onSuccess?: () => void;
}

type Step = 'select-member' | 'select-action' | 'processing';

export default function MemberAttendanceModal({ onClose, branchId, onSuccess }: MemberAttendanceModalProps) {
  const [step, setStep] = useState<Step>('select-member');
  const [searchTerm, setSearchTerm] = useState('');
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step === 'select-member' && searchRef.current) {
      searchRef.current.focus();
    }
  }, [step]);

  // Search members
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm.length >= 3) {
        searchMembers(searchTerm);
      } else {
        setMembers([]);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const searchMembers = async (query: string) => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const res = await fetch(`${API_BASE_URL}/api/members?searchTerm=${query}&limit=5`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (res.ok) {
        const d = await res.json();
        setMembers(d.data || []);
      }
    } catch {
      toast.error('Failed to search members');
    } finally {
      setLoading(false);
    }
  };

  const handleMemberSelect = (member: Member) => {
    setSelectedMember(member);
    setStep('select-action');
  };

  const handleAction = async (action: 'checkin' | 'checkout') => {
    if (!selectedMember) return;
    setStep('processing');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const res = await fetch(`${API_BASE_URL}/api/attendance/checkin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          phone: selectedMember.phoneNumber,
          branchId: branchId === 'all' ? undefined : branchId,
          action
        })
      });

      const d = await res.json();
      if (res.ok) {
        toast.success(d.message || `Member ${action} successfully`);
        onSuccess?.();
        onClose();
      } else {
        toast.error(d.error || `Failed to ${action} member`);
        setStep('select-action');
      }
    } catch {
      toast.error('Attendance recording failed');
      setStep('select-action');
    }
  };

  const handleBack = () => {
    if (step === 'select-action') {
      setSelectedMember(null);
      setStep('select-member');
    }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/70 backdrop-blur-xl animate-in fade-in duration-500">
      <div className="bg-white w-full max-w-md rounded-[48px] shadow-3xl overflow-hidden border border-zinc-100 flex flex-col h-[600px] animate-in zoom-in-95 duration-500">
        
        {/* Header Strip */}
        <div className="h-2 bg-gradient-to-r from-[#AAD816] to-[#8AAB12] w-full" />
        
        <div className="flex-1 flex flex-col min-h-0">
          {/* Header */}
          <div className="px-10 pt-10 pb-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              {step === 'select-action' && (
                <button 
                  onClick={handleBack}
                  className="w-12 h-12 flex items-center justify-center bg-zinc-50 border border-zinc-100 rounded-2xl text-zinc-400 hover:text-black hover:bg-zinc-100 transition-all active:scale-90"
                >
                  <ChevronLeft size={20} />
                </button>
              )}
              <div>
                <h2 className="text-3xl font-black text-zinc-900 tracking-tighter uppercase leading-none">Identity</h2>
                <p className="text-[10px] font-black text-zinc-400 uppercase tracking-[0.2em] mt-2">Manual Verification</p>
              </div>
            </div>
            {step !== 'processing' && (
              <button onClick={onClose} className="w-12 h-12 flex items-center justify-center bg-zinc-50 border border-zinc-100 rounded-2xl text-zinc-400 hover:text-black hover:bg-zinc-100 transition-all">
                <X size={20} />
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto px-10 pb-10">
            {step === 'select-member' && (
              <div className="space-y-8 animate-in slide-in-from-left-8 duration-500">
                {/* Search Field */}
                <div className="relative group">
                  <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-zinc-300 group-focus-within:text-[#AAD816] transition-colors" size={20} />
                  <input
                    ref={searchRef}
                    type="text"
                    placeholder="Search by name or phone..."
                    className="w-full bg-zinc-50 border-none rounded-[28px] py-6 pl-16 pr-8 text-sm font-black text-zinc-950 placeholder:text-zinc-300 focus:ring-4 focus:ring-[#AAD816]/5 transition-all shadow-inner"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {loading && (
                    <div className="absolute right-6 top-1/2 -translate-y-1/2">
                      <Loader2 className="animate-spin text-[#AAD816]" size={20} />
                    </div>
                  )}
                </div>

                {/* Results List */}
                <div className="space-y-3">
                  {members.length > 0 ? (
                    members.map(m => (
                      <button
                        key={m.id}
                        onClick={() => handleMemberSelect(m)}
                        className="w-full group flex items-center justify-between p-5 bg-white border border-zinc-100 rounded-[32px] hover:border-[#AAD816] hover:shadow-xl hover:shadow-[#AAD816]/5 transition-all duration-300 text-left active:scale-98"
                      >
                        <div className="flex items-center gap-5">
                          <div className="w-14 h-14 rounded-2xl bg-zinc-900 flex items-center justify-center text-[#AAD816] font-black text-lg shadow-lg relative overflow-hidden group-hover:rotate-3 transition-transform">
                            {m.profilePhoto ? (
                              <img src={m.profilePhoto} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span>{m.fullName.charAt(0)}</span>
                            )}
                          </div>
                          <div>
                            <p className="text-base font-black text-zinc-950 uppercase tracking-tight mb-1">{m.fullName}</p>
                            <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">{m.phoneNumber}</p>
                          </div>
                        </div>
                        <div className="w-10 h-10 rounded-full bg-zinc-50 flex items-center justify-center text-zinc-300 group-hover:bg-[#AAD816] group-hover:text-black transition-all">
                           <ChevronLeft className="rotate-180" size={16} />
                        </div>
                      </button>
                    ))
                  ) : searchTerm.length >= 3 && !loading ? (
                    <div className="py-16 text-center bg-zinc-50 rounded-[40px] border border-dashed border-zinc-200">
                      <p className="text-zinc-400 text-xs font-black uppercase tracking-widest">No matching profiles</p>
                    </div>
                  ) : (
                    <div className="py-16 text-center text-zinc-300">
                       <User size={48} className="mx-auto mb-4 opacity-10" />
                       <p className="text-[10px] font-black uppercase tracking-[0.3em]">Awaiting input</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {step === 'select-action' && selectedMember && (
              <div className="space-y-10 animate-in slide-in-from-right-8 duration-500">
                {/* Profile Card */}
                <div className="bg-zinc-900 rounded-[40px] p-8 text-center relative overflow-hidden shadow-2xl">
                  <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-[#AAD816]/10 to-transparent" />
                  <div className="w-24 h-24 rounded-[32px] bg-white border-4 border-zinc-800 shadow-2xl flex items-center justify-center overflow-hidden mx-auto mb-6 relative z-10">
                    {selectedMember.profilePhoto ? (
                      <img src={selectedMember.profilePhoto} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-4xl font-black text-zinc-200">{selectedMember.fullName.charAt(0)}</span>
                    )}
                  </div>
                  <h3 className="text-2xl font-black text-white uppercase tracking-tight relative z-10">{selectedMember.fullName}</h3>
                  <div className="flex items-center justify-center gap-3 mt-2 relative z-10">
                    <div className="px-3 py-1 bg-white/5 border border-white/10 rounded-full flex items-center gap-2">
                       <Phone size={10} className="text-[#AAD816]" />
                       <span className="text-[10px] font-bold text-zinc-400 tracking-wider font-mono">{selectedMember.phoneNumber}</span>
                    </div>
                    {branchId && branchId !== 'all' && (
                       <div className="px-3 py-1 bg-[#AAD816]/10 border border-[#AAD816]/20 rounded-full flex items-center gap-2 text-[#AAD816]">
                          <MapPin size={10} />
                          <span className="text-[10px] font-black uppercase tracking-widest">This Location</span>
                       </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="grid grid-cols-1 gap-4">
                  <button
                    onClick={() => handleAction('checkin')}
                    className="group relative flex items-center justify-between p-8 bg-[#AAD816] rounded-[32px] hover:bg-black hover:text-[#AAD816] transition-all duration-500 overflow-hidden active:scale-95 shadow-xl shadow-[#AAD816]/10"
                  >
                    <div className="flex items-center gap-6 relative z-10">
                       <div className="w-14 h-14 bg-black/10 rounded-2xl flex items-center justify-center group-hover:bg-[#AAD816]/10 transition-colors">
                          <CheckCircle2 size={24} className="text-black group-hover:text-[#AAD816]" />
                       </div>
                       <div className="text-left">
                          <span className="block text-lg font-black uppercase tracking-tight leading-none group-hover:text-white transition-colors">Clock In</span>
                          <span className="text-[9px] font-bold uppercase tracking-widest opacity-60">Identity verification pass</span>
                       </div>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-black/5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all -translate-x-4 group-hover:translate-x-0">
                       <ChevronLeft className="rotate-180" size={16} />
                    </div>
                  </button>

                  <button
                    onClick={() => handleAction('checkout')}
                    className="group relative flex items-center justify-between p-8 bg-zinc-50 border border-zinc-100 rounded-[32px] hover:bg-zinc-100 transition-all duration-500 overflow-hidden active:scale-95"
                  >
                    <div className="flex items-center gap-6 relative z-10">
                       <div className="w-14 h-14 bg-white shadow-sm border border-zinc-100 rounded-2xl flex items-center justify-center text-zinc-400 group-hover:text-zinc-950 transition-colors">
                          <LogOut size={24} />
                       </div>
                       <div className="text-left">
                          <span className="block text-lg font-black uppercase tracking-tight leading-none text-zinc-950">Clock Out</span>
                          <span className="text-[9px] font-bold uppercase tracking-widest text-zinc-400">Exit and sync session</span>
                       </div>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-zinc-200 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all -translate-x-4 group-hover:translate-x-0">
                       <ChevronLeft className="rotate-180" size={16} />
                    </div>
                  </button>
                </div>
              </div>
            )}

            {step === 'processing' && (
              <div className="h-full flex flex-col items-center justify-center text-center animate-in fade-in duration-700">
                <div className="relative w-32 h-32 mb-10">
                   <div className="absolute inset-0 rounded-full border-2 border-zinc-100" />
                   <div className="absolute inset-0 rounded-full border-t-4 border-[#AAD816] animate-spin" />
                   <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 bg-zinc-950 rounded-[28px] flex items-center justify-center shadow-2xl">
                         <div className="w-3 h-3 bg-[#AAD816] rounded-full animate-pulse shadow-[0_0_15px_#AAD816]" />
                      </div>
                   </div>
                </div>
                <h3 className="text-3xl font-black text-zinc-950 uppercase tracking-tighter">Syncing Space</h3>
                <p className="text-[10px] font-black text-zinc-400 uppercase tracking-[0.3em] mt-4">Validating session with core nodes</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
